import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import "./MenuLateral.scss";
import {
  SquarePen,
  ClipboardList,
  Settings,
  LogOut,
  Plus,
  Calendar,
  ArrowLeft,
} from "lucide-react";

import {
  getUsuarioLogado,
  getInstituicaoById,
  setDisciplinaAtual,
} from "../../utils/storageData";
import { listarResponsaveis } from "../../api/turmas";
import { useAuth } from "../../auth/AuthContext";
import { formatContatoDisplay } from "../../utils/formatting";
import { corDaTurma, salvarCorTurma } from "../../utils/coresTurma";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

// Path relativo do backend (`/admin/uploads/...`) precisa do prefixo de API.
// URLs absolutas (http://, https://) passam direto.
function resolverLogoSrc(valor) {
  if (!valor) return "";
  if (/^https?:\/\//i.test(valor)) return valor;
  return `${API_BASE}${valor}`;
}

const MenuLateral = () => {
  const navigate = useNavigate();
  const { sair, usuario: usuarioAuth } = useAuth();
  const location = useLocation();

  const [perfilAberto, setPerfilAberto] = useState(false);
  // Múltiplas boxes podem ficar abertas simultaneamente (spec).
  const [disciplinasAbertas, setDisciplinasAbertas] = useState(() => new Set());
  const toggleDisciplinaAberta = (id) =>
    setDisciplinasAbertas((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const [paletaAbertaId, setPaletaAbertaId] = useState(null);
  const [corTemporaria, setCorTemporaria] = useState("#76A8D9");
  const [dataVersion, setDataVersion] = useState(0);
  const colorInputRef = useRef(null);
  const [disciplinaAtualId, setDisciplinaAtualId] = useState(
    () => localStorage.getItem("disciplinaAtualId") || "",
  );
  const [coordenacaoAtualId, setCoordenacaoAtualIdState] = useState(
    () => localStorage.getItem("coordenacaoAtualId") || "",
  );

  // Spec: extensões acadêmicas (turmas secundárias) sempre abertas ao carregar
  // a plataforma — ignora estado salvo. Toggle funciona dentro da sessão atual.
  const [mostrarSecundarias, setMostrarSecundarias] = useState(true);

  const isActive = (path) => location.pathname === path;

  const abrirSeletorCor = () => {
    if (colorInputRef.current) {
      colorInputRef.current.click();
    }
  };

  useEffect(() => {
    const salvo = localStorage.getItem("menuAlunoSecundariasVisiveis");

    if (salvo === null) {
      localStorage.setItem("menuAlunoSecundariasVisiveis", "true");
    }
  }, []);

  useEffect(() => {
    const bump = () => setDataVersion((v) => v + 1);

    const syncDisciplinaAtual = () => {
      setDisciplinaAtualId(localStorage.getItem("disciplinaAtualId") || "");
    };

    const onStorage = (e) => {
      if (!e.key) {
        bump();
        syncDisciplinaAtual();
        return;
      }

      if (
        e.key === "usuarios_override" ||
        e.key === "turmas_override" ||
        e.key === "turma_alunos_override" ||
        e.key === "usuarios_disciplinas_override" ||
        e.key === "eventos_override" ||
        e.key === "usuario"
      ) {
        bump();
      }

      if (e.key === "disciplinaAtualId") {
        syncDisciplinaAtual();
      }
    };

    window.addEventListener("app:data-changed", bump);
    window.addEventListener("disciplinaAtual:changed", syncDisciplinaAtual);
    window.addEventListener("storage", onStorage);

    return () => {
      window.removeEventListener("app:data-changed", bump);
      window.removeEventListener(
        "disciplinaAtual:changed",
        syncDisciplinaAtual,
      );
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const user = useMemo(() => {
    if (dataVersion < 0) return null;
    const base = getUsuarioLogado();
    if (!base) return null;
    // usuarioAuth vem fresco do /usuarios/me (AuthContext). getUsuarioLogado lê
    // do localStorage e pode estar defasado no primeiro render. Sobrepõe os
    // vínculos vindos do backend para não usar dado velho (ex.: turmasComoAluno
    // ausente reativaria o bug do aluno-só-responsável aparecendo como aluno).
    if (usuarioAuth && String(usuarioAuth.id) === String(base.id)) {
      return {
        ...base,
        turmas: usuarioAuth.turmas ?? base.turmas,
        turmasComoResponsavel:
          usuarioAuth.turmasComoResponsavel ?? base.turmasComoResponsavel,
        turmasComoAluno: usuarioAuth.turmasComoAluno ?? base.turmasComoAluno,
        coordenacoesComoCoordenador:
          usuarioAuth.coordenacoesComoCoordenador ??
          base.coordenacoesComoCoordenador,
      };
    }
    return base;
  }, [dataVersion, usuarioAuth]);

  const instituicao = useMemo(() => {
    if (!user) return null;
    if (dataVersion < 0) return null;
    return getInstituicaoById(user.instituicaoId || user.faculdadeId);
  }, [user, dataVersion]);

  useEffect(() => {
    localStorage.setItem(
      "menuAlunoSecundariasVisiveis",
      String(mostrarSecundarias),
    );

    window.dispatchEvent(new Event("menuAlunoSecundarias:changed"));
  }, [mostrarSecundarias]);

  function gerarCorAleatoria() {
    const letras = "0123456789ABCDEF";
    let cor = "#";

    for (let i = 0; i < 6; i++) {
      cor += letras[Math.floor(Math.random() * 16)];
    }

    return cor;
  }

  function safeJsonParse(value, fallback) {
    try {
      return JSON.parse(value) ?? fallback;
    } catch {
      return fallback;
    }
  }

  function isResponsavelEmAlgunaTurma(usuario) {
    if (!usuario) return false;

    // Backend: aluno.responsável tem entries em VinculoResponsavel.
    // /usuarios/me retorna `turmasComoResponsavel: Guid[]` com esses ids.
    if (Array.isArray(usuario.turmasComoResponsavel)) {
      return usuario.turmasComoResponsavel.length > 0;
    }

    // Fallback mock: localStorage turmas_override (transição Bloco 8→9).
    const uid = Number(usuario.id);
    const userStr = String(usuario.user || "");

    try {
      const turmasOverride = safeJsonParse(
        localStorage.getItem("turmas_override"),
        [],
      );
      if (Array.isArray(turmasOverride)) {
        return turmasOverride.some((disc) => {
          const ids = Array.isArray(disc.responsavelIds)
            ? disc.responsavelIds.map(Number)
            : [];
          if (ids.includes(uid)) return true;

          const resps = Array.isArray(disc.responsaveis)
            ? disc.responsaveis
            : [];
          return resps.some(
            (r) => Number(r.usuarioId) === uid || String(r.userId) === userStr,
          );
        });
      }
    } catch {}

    return false;
  }

  const tipoUsuario = String(user?.tipo || "").toLowerCase();

  const rotaComMenuAluno =
    location.pathname === "/dashboard" ||
    location.pathname.startsWith("/detalhe-calendario-aluno");

  const alunoVirouResponsavelTemp =
    tipoUsuario === "aluno" && isResponsavelEmAlgunaTurma(user);

  // Fluxo definido pela rota. Aluno-responsável vê o fluxo responsável nas
  // rotas de responsável (criar-evento, eventos, editar-turma) e o fluxo aluno
  // na home (/dashboard). "+" na dashboard leva à rota responsável; "Voltar
  // para a página inicial" retorna à home.
  const usarFluxoAluno = rotaComMenuAluno && tipoUsuario === "aluno";

  // Lista de turmas do usuário vem inteira de /usuarios/me (user.turmas).
  // Para aluno.responsável fora do fluxo aluno, filtra apenas turmas onde
  // ele tem cargo de responsável (user.turmasComoResponsavel).
  const disciplinasDoUsuario = useMemo(() => {
    if (!user) return [];
    const turmas = Array.isArray(user.turmas) ? user.turmas : [];

    const formatar = (t) => ({
      id: t.id,
      nome: t.nome,
      cor: corDaTurma(t.id),
      tipo: String(t.tipo || "").toLowerCase(),
      complemento: t.complemento,
    });
    const porNome = (a, b) =>
      String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR");

    // Fluxo responsável (aluno-responsável fora da home): só turmas onde é responsável.
    if (alunoVirouResponsavelTemp && !usarFluxoAluno) {
      const idsResp = new Set(
        (Array.isArray(user.turmasComoResponsavel)
          ? user.turmasComoResponsavel
          : []
        ).map(String),
      );
      return turmas
        .filter((t) => idsResp.has(String(t.id)))
        .map(formatar)
        .sort(porNome);
    }

    // Fluxo aluno: só as turmas onde ele é ALUNO (box no menu lateral). Quem é
    // só responsável da turma (não aluno) não vê a box aqui — só pelo "+".
    // Se backend não enviar turmasComoAluno (contrato antigo), usa todas.
    if (tipoUsuario === "aluno" && Array.isArray(user.turmasComoAluno)) {
      const idsAluno = new Set(user.turmasComoAluno.map(String));
      return turmas
        .filter((t) => idsAluno.has(String(t.id)))
        .map(formatar)
        .sort(porNome);
    }

    return turmas.map(formatar).sort(porNome);
  }, [user, alunoVirouResponsavelTemp, usarFluxoAluno, tipoUsuario]);

  // Cache de responsáveis por turma — usado para exibir cargo+contato no
  // fluxo aluno. Spec: só exibe quando cargo E contato preenchidos.
  const [responsaveisPorTurma, setResponsaveisPorTurma] = useState({});

  // Busca responsáveis de todas as turmas do aluno. Merge incremental para
  // evitar flicker e refletir mudanças (cargo/contato/add/remove) dinamicamente.
  const refetchResponsaveis = useCallback(async () => {
    if (!usarFluxoAluno || disciplinasDoUsuario.length === 0) return;
    const ids = disciplinasDoUsuario.map((t) => t.id);
    const entries = await Promise.all(
      ids.map(async (id) => {
        try {
          const lista = await listarResponsaveis(id);
          const filtrados = (lista || []).filter(
            (r) => r.cargo && r.contato && String(r.contato).trim() !== "",
          );
          return [id, filtrados];
        } catch {
          return [id, null];
        }
      }),
    );
    setResponsaveisPorTurma((prev) => {
      const idsAtuais = new Set(ids.map(String));
      // remove turmas que saíram da lista
      const next = Object.fromEntries(
        Object.entries(prev).filter(([k]) => idsAtuais.has(String(k))),
      );
      for (const [id, lista] of entries) {
        if (lista !== null) next[id] = lista;
      }
      return next;
    });
  }, [usarFluxoAluno, disciplinasDoUsuario]);

  useEffect(() => {
    refetchResponsaveis();
  }, [refetchResponsaveis]);

  // Refetch periódico + ao focar a janela + em eventos de mudança de turma
  // → reflete dinamicamente quando coord/responsável edita cargo/contato,
  //   adiciona/remove responsável da turma.
  useEffect(() => {
    if (!usarFluxoAluno || disciplinasDoUsuario.length === 0) return undefined;
    const intervalo = setInterval(refetchResponsaveis, 15000);
    const onFocus = () => refetchResponsaveis();
    const onVis = () => {
      if (document.visibilityState === "visible") refetchResponsaveis();
    };
    window.addEventListener("focus", onFocus);
    window.addEventListener("turmas:changed", onFocus);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(intervalo);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("turmas:changed", onFocus);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [usarFluxoAluno, disciplinasDoUsuario, refetchResponsaveis]);

  const disciplinasComResponsaveis = useMemo(() => {
    return disciplinasDoUsuario.map((disc) => {
      const responsaveis = responsaveisPorTurma[disc.id] || [];
      // Spec: exibe na dashboard do aluno só responsáveis com cargo E contato
      // preenchidos (cargo também é texto livre, então aparece como label).
      const responsaveisCompletos = responsaveis
        .filter(
          (r) =>
            r.cargo &&
            String(r.cargo).trim() !== "" &&
            r.contato &&
            String(r.contato).trim() !== "",
        )
        .map((r) => ({
          nome: r.nome,
          cargo: String(r.cargo).trim(),
          contato: r.contato,
        }));
      return { ...disc, _merged: { responsaveisCompletos } };
    });
  }, [disciplinasDoUsuario, responsaveisPorTurma]);

  useEffect(() => {
    if (!disciplinasDoUsuario.length) {
      setDisciplinaAtualId("");
      localStorage.removeItem("disciplinaAtualId");
      window.dispatchEvent(new Event("disciplinaAtual:changed"));
      return;
    }

    const existeNaLista = disciplinasDoUsuario.some(
      (d) => String(d.id) === String(disciplinaAtualId),
    );

    if (!disciplinaAtualId || !existeNaLista) {
      const firstId = String(disciplinasDoUsuario[0].id);
      setDisciplinaAtualId(firstId);
      localStorage.setItem("disciplinaAtualId", firstId);
      window.dispatchEvent(new Event("disciplinaAtual:changed"));
    }
  }, [disciplinaAtualId, disciplinasDoUsuario]);

  // Coordenações vinculadas (vêm de /usuarios/me como [{id, nome}]).
  const coordenacoesDoCoordenador = Array.isArray(
    user?.coordenacoesComoCoordenador,
  )
    ? user.coordenacoesComoCoordenador.filter((c) => c && c.id && c.nome)
    : [];

  // Sincroniza coordenação atual no localStorage: aponta para uma coordenação válida.
  useEffect(() => {
    if (coordenacoesDoCoordenador.length === 0) {
      if (coordenacaoAtualId) {
        setCoordenacaoAtualIdState("");
        localStorage.removeItem("coordenacaoAtualId");
        window.dispatchEvent(new Event("coordenacaoAtual:changed"));
      }
      return;
    }
    const existe = coordenacoesDoCoordenador.some(
      (c) => String(c.id) === String(coordenacaoAtualId),
    );
    if (!coordenacaoAtualId || !existe) {
      const firstId = String(coordenacoesDoCoordenador[0].id);
      setCoordenacaoAtualIdState(firstId);
      localStorage.setItem("coordenacaoAtualId", firstId);
      window.dispatchEvent(new Event("coordenacaoAtual:changed"));
    }
  }, [coordenacaoAtualId, coordenacoesDoCoordenador]);

  if (!user) return null;

  const primeiraLetra = String(user.nome || "")
    .trim()
    .charAt(0)
    .toUpperCase();

  const alunoVirouResponsavel =
    tipoUsuario === "aluno" && isResponsavelEmAlgunaTurma(user);

  const isAluno = usarFluxoAluno;

  const isCoordenador = tipoUsuario === "coordenador";
  // Coordenador: vínculo é a coordenação. Responsável: vínculo é a turma.
  const coordSemCoordenacao =
    isCoordenador &&
    Array.isArray(user?.coordenacoesComoCoordenador) &&
    user.coordenacoesComoCoordenador.length === 0;
  const respSemTurma =
    tipoUsuario === "responsavel" &&
    Array.isArray(user?.turmas) &&
    user.turmas.length === 0;
  const usuarioSemVinculo = coordSemCoordenacao || respSemTurma;
  const coordenadorSemVinculo = usuarioSemVinculo;
  const rotaSemTurma = location.pathname === "/sem-turma";
  // Responsável puro não tem dashboard inicial — só aluno-virou-responsável
  // precisa do botão pra voltar ao dashboard de aluno. Spec: o botão é fixo em
  // todo o fluxo responsável (criar-evento, eventos, editar-turma...), some só
  // na própria dashboard de aluno e em /sem-turma.
  const mostrarBotaoVoltarInicio =
    alunoVirouResponsavel && !rotaComMenuAluno && !rotaSemTurma;
  // Sempre exibe Editar Turma para responsável, exceto em /sem-turma (não há turma para editar).
  const mostrarBotaoEditarTurma = !rotaSemTurma;

  const handleLogout = async (e) => {
    e.stopPropagation();
    try {
      await sair();
    } finally {
      navigate("/");
    }
  };

  const isEditarTurmaAtivo =
    location.pathname === "/editar-turma" ||
    location.pathname === "/editar-turma-coordenador" ||
    location.pathname.startsWith("/editar-turma/");

  const getTituloCurto = (nome) => {
    const texto = String(nome || "").trim();

    return texto
      .replace(/\s*-\s*Turma\s+\d+$/i, "")
      .replace(/^Monitoria do Curso de\s+/i, "Monitoria ")
      .replace(/^Introdução aos?\s+/i, "")
      .trim();
  };

  const getCorDisciplinaUsuario = (disc) => disc?.cor || corDaTurma(disc?.id);

  const salvarCorDisciplinaUsuario = (disciplinaId, novaCor) => {
    // Espelha local (UI responde na hora) e persiste no backend para
    // sincronizar a cor entre os dispositivos do usuário.
    salvarCorTurma(disciplinaId, novaCor);
    setDataVersion((v) => v + 1);
  };

  const ehSecundaria = (disc) => {
    // Backend serializa enum como "primario"/"secundario" (camelCase).
    // Mock antigo usava "secundaria"/"primaria" feminino. Aceita os dois
    // pra cobrir transição.
    const t = String(disc.tipo || "").toLowerCase();
    return t === "secundario" || t === "secundaria";
  };

  const disciplinasPrimarias = isAluno
    ? disciplinasComResponsaveis.filter((disc) => !ehSecundaria(disc))
    : [];

  const disciplinasSecundarias = isAluno
    ? disciplinasComResponsaveis.filter((disc) => ehSecundaria(disc))
    : [];

  const renderDisciplinaAluno = (disc) => {
    const open = disciplinasAbertas.has(disc.id);
    const paletaAberta = paletaAbertaId === disc.id;
    const corAtual = getCorDisciplinaUsuario(disc);

    return (
      <div
        key={disc.id}
        className={`menuLateral__materiaWrap ${open ? "open" : ""}`}
        style={{ "--discColor": corAtual }}
      >
        <div className={`menuLateral__materiaItem ${open ? "open" : ""}`}>
          <button
            type="button"
            className="menuLateral__dotBtn"
            onClick={(e) => {
              e.stopPropagation();
              setPaletaAbertaId((prev) => {
                const vaiAbrir = prev !== disc.id;
                if (vaiAbrir) setCorTemporaria(corAtual);
                return vaiAbrir ? disc.id : null;
              });
            }}
            aria-label="Alterar cor da turma"
            title="Alterar cor da turma"
          >
            <span className="menuLateral__dot" />
          </button>

          <button
            type="button"
            className="menuLateral__materiaMain"
            onClick={() => toggleDisciplinaAberta(disc.id)}
          >
            <span className="menuLateral__materiaTitle">
              {getTituloCurto(disc.nome)}
            </span>
            <span className="menuLateral__materiaToggle">
              {open ? "–" : "+"}
            </span>
          </button>
        </div>

        {paletaAberta && (
          <>
            <button
              type="button"
              className="menuLateral__colorBackdrop"
              onClick={() => setPaletaAbertaId(null)}
              aria-label="Fechar seletor de cor"
            />

            <div
              className="menuLateral__colorModal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="menuLateral__colorModalHeader">
                <span className="menuLateral__colorModalTitle">
                  Selecione sua cor favorita
                </span>
              </div>

              <div className="menuLateral__colorModalBody">
                <input
                  ref={colorInputRef}
                  type="color"
                  value={corTemporaria}
                  onChange={(e) =>
                    setCorTemporaria(e.target.value.toUpperCase())
                  }
                  className="menuLateral__colorNativeInput"
                />

                <button
                  type="button"
                  className="menuLateral__colorPickerTrigger"
                  onClick={abrirSeletorCor}
                >
                  <span
                    className="menuLateral__colorPickerPreview"
                    style={{ backgroundColor: corTemporaria }}
                  />
                  <span className="menuLateral__colorPickerText">
                    Escolher cor
                  </span>
                </button>

                <div className="menuLateral__colorInfo">
                  <span
                    className="menuLateral__colorPreviewLarge"
                    style={{ backgroundColor: corTemporaria }}
                  />
                  <div className="menuLateral__colorInfoTexts">
                    <strong>{String(corTemporaria).toUpperCase()}</strong>
                    <span>Cor da turma</span>
                  </div>
                </div>
              </div>

              <div className="menuLateral__colorModalActions">
                <button
                  type="button"
                  className="menuLateral__colorBtn menuLateral__colorBtn--ghost"
                  onClick={() => {
                    const novaCor = gerarCorAleatoria();
                    salvarCorDisciplinaUsuario(disc.id, novaCor);
                    setPaletaAbertaId(null);
                  }}
                >
                  Escolha automática
                </button>

                <button
                  type="button"
                  className="menuLateral__colorBtn menuLateral__colorBtn--ghost"
                  onClick={() => setPaletaAbertaId(null)}
                >
                  Cancelar
                </button>

                <button
                  type="button"
                  className="menuLateral__colorBtn menuLateral__colorBtn--primary"
                  onClick={() => {
                    salvarCorDisciplinaUsuario(disc.id, corTemporaria);
                    setPaletaAbertaId(null);
                  }}
                >
                  Salvar cor
                </button>
              </div>
            </div>
          </>
        )}

        {open && (
          <div className="menuLateral__materiaDetails">
            <div className="menuLateral__detailsTitle">
              {(() => {
                const nome = String(disc.nome || "").trim();
                const comp = String(disc.complemento || "").trim();
                return comp ? `${nome} - ${comp}` : nome;
              })()}
            </div>
            {(disc._merged?.responsaveisCompletos || []).map((item, i) => (
              <div key={`r-${i}`} className="menuLateral__detailsLine">
                <strong>{`${item.cargo}:`}</strong>{" "}
                <span className="menuLateral__detailsValue">
                  {item.nome || "—"}
                  <br />
                  {formatContatoDisplay(item.contato)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <aside className="menuLateral">
      <header
        className={`menuLateral__header ${perfilAberto ? "active" : ""}`}
        onClick={() => setPerfilAberto((prev) => !prev)}
      >
        <div className="menuLateral__avatar">{primeiraLetra}</div>

        {!perfilAberto ? (
          <div className="menuLateral__brand">
            <span>AgendaUni - sua rotina educacional sob controle!</span>
          </div>
        ) : (
          <>
            <div className="menuLateral__user-details">
              <strong className="menuLateral__user-title">{user.nome}</strong>

              {user?.user && (
                <span className="menuLateral__user-sub">{user.user}</span>
              )}

              {user?.login && (
                <span className="menuLateral__user-sub">{user.login}</span>
              )}
            </div>

            <button
              type="button"
              className="menuLateral__btn-sair"
              onClick={handleLogout}
            >
              <LogOut size={16} />
              <span>Sair</span>
            </button>
          </>
        )}
      </header>

      {!perfilAberto && (
        <div className="menuLateral__uni-card">
          <div className="menuLateral__uni-logo">
            {instituicao?.logoUrl || instituicao?.logo ? (
              <img
                src={resolverLogoSrc(instituicao.logoUrl || instituicao.logo)}
                alt={instituicao?.nome || "Logo"}
              />
            ) : (
              <span
                className="menuLateral__uni-logo-fallback"
                aria-hidden="true"
              >
                {String(instituicao?.sigla || instituicao?.nome || "?")
                  .slice(0, 2)
                  .toUpperCase()}
              </span>
            )}
          </div>

          <div className="menuLateral__uni-info">
            <span className="menuLateral__uni-label">{instituicao?.nome}</span>
            <strong className="menuLateral__uni-name">
              {instituicao?.sigla}
            </strong>
          </div>
        </div>
      )}

      <hr className="menuLateral__divider" />

      <nav className="menuLateral__menu">
        <div className="menuLateral__actions">
          {!perfilAberto &&
            !isAluno &&
            tipoUsuario !== "admin" &&
            (isCoordenador ? (
              <div className="menuLateral__turmaBox">
                <select
                  className="menuLateral__turmaSelect"
                  value={coordenacaoAtualId}
                  onChange={(e) => {
                    const next = e.target.value;
                    if (String(next) === String(coordenacaoAtualId)) return;
                    setCoordenacaoAtualIdState(next);
                    localStorage.setItem("coordenacaoAtualId", next);
                    window.dispatchEvent(new Event("coordenacaoAtual:changed"));
                    Swal.fire({
                      toast: true,
                      position: "top-end",
                      icon: "success",
                      title: "Alterado",
                      showConfirmButton: false,
                      timer: 1800,
                      timerProgressBar: true,
                    });
                  }}
                >
                  {coordenacoesDoCoordenador.map((c) => (
                    <option key={c.id} value={String(c.id)}>
                      {c.nome}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="menuLateral__turmaBox">
                <span className="menuLateral__turmaLabel">Turma atual</span>
                <select
                  className="menuLateral__turmaSelect"
                  value={disciplinaAtualId}
                  onChange={(e) => {
                    const next = e.target.value;
                    if (String(next) === String(disciplinaAtualId)) return;

                    setDisciplinaAtualId(next);
                    setDisciplinaAtual(next);

                    Swal.fire({
                      toast: true,
                      position: "top-end",
                      icon: "success",
                      title: "Alterado",
                      showConfirmButton: false,
                      timer: 1800,
                      timerProgressBar: true,
                    });

                    if (
                      location.pathname.startsWith("/editar-turma-coordenador")
                    ) {
                      navigate(`/editar-turma-coordenador/${next}`);
                      return;
                    }

                    if (location.pathname.startsWith("/editar-turma")) {
                      navigate(`/editar-turma/${next}`);
                    }
                  }}
                >
                  {disciplinasDoUsuario.map((d) => {
                    const nome = String(d.nome || "").trim();
                    const comp = String(d.complemento || "").trim();
                    const label = comp ? `${nome} - ${comp}` : nome;
                    return (
                      <option key={d.id} value={String(d.id)}>
                        {label}
                      </option>
                    );
                  })}
                </select>
              </div>
            ))}

          {isAluno ? (
            <div className="menuLateral__materias">
              {disciplinasPrimarias.map(renderDisciplinaAluno)}

              {!!disciplinasSecundarias.length && (
                <div className="menuLateral__extensoes">
                  <button
                    type="button"
                    className={`menuLateral__extensoesToggle ${
                      mostrarSecundarias ? "is-open" : "is-closed"
                    }`}
                    onClick={() => setMostrarSecundarias((prev) => !prev)}
                    aria-label={
                      mostrarSecundarias
                        ? "Ocultar turmas secundárias"
                        : "Mostrar turmas secundárias"
                    }
                  >
                    <span className="menuLateral__extensoesText">
                      Extensões acadêmica
                    </span>
                    <span
                      className={`menuLateral__extensoesEye ${
                        mostrarSecundarias ? "is-open" : "is-closed"
                      }`}
                      aria-hidden="true"
                    />
                  </button>

                  {mostrarSecundarias && (
                    <div className="menuLateral__extensoesList">
                      {disciplinasSecundarias.map(renderDisciplinaAluno)}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : tipoUsuario === "admin" ? (
            <>
              <button
                className={`menuLateral__btn ${isActive("/admin/instituicoes") ? "active" : ""}`}
                onClick={() => navigate("/admin/instituicoes")}
              >
                <Settings size={20} />
                <span>Painel admin</span>
              </button>
            </>
          ) : isCoordenador ? (
            <>
              <button
                className={`menuLateral__btn ${isActive("/criar-evento") ? "active" : ""}`}
                onClick={() => navigate("/criar-evento")}
                disabled={coordenadorSemVinculo}
              >
                <SquarePen size={20} />
                <span>Criar Eventos</span>
              </button>

              <button
                className={`menuLateral__btn ${isActive("/eventos") ? "active" : ""}`}
                onClick={() => navigate("/eventos")}
                disabled={coordenadorSemVinculo}
              >
                <ClipboardList size={20} />
                <span>Eventos Publicados</span>
              </button>

              <button
                className={`menuLateral__btn ${isActive("/nova-turma") ? "active" : ""}`}
                onClick={() => navigate("/nova-turma")}
                disabled={coordenadorSemVinculo}
              >
                <Plus size={20} />
                <span>Criar Turmas</span>
              </button>

              <button
                className={`menuLateral__btn ${isEditarTurmaAtivo ? "active" : ""}`}
                onClick={() => navigate("/editar-turma-coordenador")}
                disabled={coordenadorSemVinculo}
              >
                <Settings size={20} />
                <span>Editar Turmas</span>
              </button>

              <button
                className={`menuLateral__btn ${isActive("/ver-calendario") ? "active" : ""}`}
                onClick={() => navigate("/ver-calendario")}
                disabled={coordenadorSemVinculo}
              >
                <Calendar size={20} />
                <span>Calendário</span>
              </button>
            </>
          ) : (
            <>
              <button
                className={`menuLateral__btn ${isActive("/criar-evento") ? "active" : ""}`}
                onClick={() => navigate("/criar-evento")}
                disabled={usuarioSemVinculo}
              >
                <SquarePen size={20} />
                <span>Criar Eventos</span>
              </button>

              <button
                className={`menuLateral__btn ${isActive("/eventos") ? "active" : ""}`}
                onClick={() => navigate("/eventos")}
                disabled={usuarioSemVinculo}
              >
                <ClipboardList size={20} />
                <span>Eventos Publicados</span>
              </button>

              <button
                className={`menuLateral__btn ${isEditarTurmaAtivo ? "active" : ""}`}
                onClick={() => {
                  if (!disciplinaAtualId) return;
                  navigate(`/editar-turma/${disciplinaAtualId}`);
                }}
                disabled={usuarioSemVinculo}
              >
                <Settings size={20} />
                <span>Editar Turma</span>
              </button>

              {mostrarBotaoVoltarInicio && (
                <button
                  className="menuLateral__btn menuLateral__btn--back"
                  onClick={() => {
                    navigate("/dashboard");
                  }}
                >
                  <ArrowLeft size={20} />
                  <span>Voltar para a página inicial</span>
                </button>
              )}
            </>
          )}
        </div>
      </nav>
    </aside>
  );
};

export default MenuLateral;
